import React, { useState, useEffect, useCallback } from 'react';
import { AuroraBackgroundInteractive } from './AuroraBackground.js';

// Access Control Component
const AccessControl = ({ onAccessGranted }) => {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const validateSlug = useCallback(async (slug) => {
    try {
      const response = await fetch(`https://www.vesaro.com/_functions/test?limit=1&slug=${slug}`);

      if (response.ok) {
        const data = await response.json();

        // Check if we got valid base config for this slug
        if (data.baseConfig && data.baseConfig.slug === slug) {
          onAccessGranted();
        } else {
          setIsValidating(false);
        }
      } else {
        setIsValidating(false);
      }
    } catch (error) {
      setIsValidating(false);
    }
  }, [onAccessGranted]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('slug');
    const adminParam = params.get('admin');

    // Check if already authenticated in this session
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated') === 'true';

    // Check if slug exists
    if (slug) {
      if (slug.toLowerCase() === 'admin') {
        // Show admin login for admin slug
        setShowAdminLogin(true);
        setIsValidating(false);
      } else if (adminParam === 'true') {
        // Admin mode with a config slug - check session authentication
        if (isAuthenticated) {
          // Already authenticated in this session - grant access immediately
          onAccessGranted();
        } else {
          // Not authenticated - show admin login
          setShowAdminLogin(true);
          setIsValidating(false);
        }
      } else {
        // Regular slug - validate it exists by trying to fetch data
        validateSlug(slug);
      }
    } else {
      // No slug provided
      setIsValidating(false);
    }
  }, [validateSlug, onAccessGranted]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError('');

    try {
      // DEVELOPMENT BYPASS: Check if running locally
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // Simple password check for local development
        // Use password "dev" when testing locally
        if (password === 'dev') {
          sessionStorage.setItem('adminToken', 'dev-token-' + Date.now());
          sessionStorage.setItem('adminAuthenticated', 'true');
          onAccessGranted();
          return;
        } else {
          setError('Incorrect password for local development. Use "dev"');
          setPassword('');
          setIsAuthenticating(false);
          return;
        }
      }

      // Call secure backend authentication function (only works when deployed on Netlify)
      const response = await fetch('/.netlify/functions/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store JWT token securely
        sessionStorage.setItem('adminToken', data.token);
        sessionStorage.setItem('adminAuthenticated', 'true');
        onAccessGranted();
      } else {
        // Handle authentication errors
        if (response.status === 429) {
          setError(data.error);
        } else if (data.attemptsLeft !== undefined) {
          setError(`Incorrect password. ${data.attemptsLeft} attempt(s) remaining.`);
        } else {
          setError(data.error || 'Authentication failed');
        }
        setPassword('');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      setPassword('');
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (isValidating) {
    return (
      <AuroraBackgroundInteractive 
        variant="dark"
        intensity="strong"
        animate={true}
        interactive={true}
        className="min-h-[100dvh] w-full flex items-center justify-center"
      >
        <div className="text-center text-white">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg font-medium">Validating access...</div>
        </div>
      </AuroraBackgroundInteractive>
    );
  }

  if (showAdminLogin) {
    return (
      <AuroraBackgroundInteractive 
        variant="dark"
        intensity="strong"
        animate={true}
        interactive={true}
        className="min-h-[100dvh] w-full flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-[20px] w-full max-w-md shadow-2xl">
          <div className="p-6 text-center">
            <div className="mb-6">
              <img 
                src="/logo.png" 
                alt="Vesaro Logo" 
                className="h-8 w-auto mx-auto mb-4"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.insertAdjacentHTML('beforeend', '<div class="text-2xl font-bold text-gray-800">VESARO</div>');
                }}
              />
              <h2 className="text-xl font-bold text-gray-800 mb-2">Admin Access</h2>
              <p className="text-sm text-gray-600">Enter the admin password to continue</p>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Admin Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isAuthenticating}
                className="w-full bg-black hover:bg-gray-800 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAuthenticating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Authenticating...</span>
                  </>
                ) : (
                  'Access Admin'
                )}
              </button>
            </form>
          </div>
        </div>
      </AuroraBackgroundInteractive>
    );
  }

  // No valid slug or access - CLEAN VERSION WITHOUT CONTACT
  return (
    <AuroraBackgroundInteractive 
      variant="dark"
      intensity="strong"
      animate={true}
      interactive={true}
      className="min-h-[100dvh] w-full flex items-center justify-center p-4"
    >
      <div className="text-center text-white max-w-md">
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">🔒</span>
          </div>
          
          <img 
            src="/logo.png" 
            alt="Vesaro Logo" 
            className="h-8 w-auto mx-auto mb-4 opacity-75"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.insertAdjacentHTML('beforeend', '<div class="text-xl font-bold text-white/75 mb-4">VESARO</div>');
            }}
          />
          
          <h2 className="text-2xl font-bold mb-4">Access Restricted</h2>
          <p className="text-white/80 mb-8 leading-relaxed">
            This configurator requires a valid access link.
          </p>

          {/* Admin Login Button Only */}
          <div>
            <p className="text-xs text-white/50 mb-3">Admin Access</p>
            <button
              onClick={() => {
                window.location.href = window.location.origin + window.location.pathname + '?slug=admin';
              }}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.029 5.912c-.563-.097-1.159-.026-1.658.33L10 17.25H6v2.5A2.5 2.5 0 003.5 22H1v-1.5A2.5 2.5 0 013.5 18V14h4.25l2.163-2.313a6 6 0 015.912-7.029A6 6 0 0119 9z" />
              </svg>
              <span className="text-sm">Admin Login</span>
            </button>
          </div>
        </div>
      </div>
    </AuroraBackgroundInteractive>
  );
};

export default AccessControl;